import { trackError } from '@vend/utilities'
import {
  SERVICE_WORKER,
  SERVICE_WORKER_EVENTS,
} from '../service-worker.constant'
import { $log, $q, $window } from '../../../ng-compat/imports'
import { EventEmitter } from '../../../util/event-emitter'

const NS = '[SERVICE-WORKER-MANAGER]'

/**
 * @class ServiceWorkerManagerService
 */
export class ServiceWorkerManagerService extends EventEmitter {
  constructor() {
    super()

    /**
     * The ServiceWorkerRegistration interface of the Service Worker.
     *
     * @static
     * @property serviceWorkerRegistration
     * @type {ServiceWorkerRegistration}
     */
    this.serviceWorkerRegistration = null

    /**
     * The currently active Service Worker.
     *
     * @static
     * @property activeServiceWorker
     * @type {ServiceWorker}
     */
    this.activeServiceWorker = null
  }

  /**
   * The ServiceWorkerContainer interface of the Service Worker.
   *
   * @static
   * @property serviceWorker
   * @type {ServiceWorkerContainer}
   */
  get serviceWorker() {
    return $window.navigator.serviceWorker
  }

  /**
   * If Service Workers are supported in the current Browser.
   *
   * @static
   * @property isServiceWorkerSupported
   * @type {Boolean}
   */
  get isServiceWorkerSupported() {
    return $window.navigator && 'serviceWorker' in $window.navigator
  }

  /**
   * Responsible for registering the Service Worker and setting up listeners which monitor its status.
   *
   * @method registerServiceWorker
   *
   * @return {Promise} Resolved once the listeners which monitor the Service Worker have been setup.
   */
  registerServiceWorker() {
    if (!this.isServiceWorkerSupported) {
      return $q.resolve()
    }

    return this.serviceWorker
      .register(`/${SERVICE_WORKER.MAIN_FILE}`)
      .then(registration => {
        if (registration.active) {
          registration.active.postMessage({
            action: SERVICE_WORKER_EVENTS.UPDATE_CACHES,
          })
        }

        this.serviceWorkerRegistration = registration

        this._monitorServiceWorker()

        $log.debug(`${NS} Registered`, registration)
      })
      .catch(error => {
        trackError(`${NS} Failed to Register`, {
          data: {
            error: (error && error.message) || 'Unknown error',
          },
        })
      })
  }

  /**
   * Requests that the Service Worker caches are updated once the Service Worker has been updated.
   *
   * @method refreshServiceWorkerCache
   */
  refreshServiceWorkerCache() {
    if (!this.isServiceWorkerSupported) {
      return
    }

    this._refreshTriggered = true

    const serviceWorker =
      this.serviceWorkerRegistration.waiting || this.activeServiceWorker
    serviceWorker.postMessage({ action: SERVICE_WORKER_EVENTS.UPDATE_ALL })
  }

  /**
   * Listens for changes in the Service Worker registration.
   *
   * @private
   * @method _monitorServiceWorker
   */
  _monitorServiceWorker() {
    this._listenForServiceWorkerEvents()

    if (
      !this.serviceWorkerRegistration ||
      !this.serviceWorkerRegistration.active
    ) {
      return
    }

    $log.debug(`${NS} Service Worker active`)

    this.activeServiceWorker = this.serviceWorkerRegistration.active

    if (this.serviceWorkerRegistration.waiting) {
      $log.debug(`${NS} New Service Worker waiting`)

      this.emit(SERVICE_WORKER_EVENTS.SERVICE_WORKER_INSTALLED)
      return
    }

    if (this.serviceWorkerRegistration.installing) {
      $log.debug(`${NS} New Service Worker installing`)

      this._trackServiceWorkerInstalling(
        this.serviceWorkerRegistration.installing
      )
      return
    }

    this.serviceWorkerRegistration.addEventListener('updatefound', event => {
      $log.debug(`${NS} Service Worker update found`, event)

      this._trackServiceWorkerInstalling(
        this.serviceWorkerRegistration.installing
      )
    })
  }

  /**
   * Listens for Service Worker events.
   *
   * @private
   * @method _listenForServiceWorkerEvents
   */
  _listenForServiceWorkerEvents() {
    /**
     * Handler for the Service Worker message event.
     *
     * @listens ServiceWorkerGlobalScope.message
     * @param {event} MessageEvent
     */
    this.serviceWorker.addEventListener('message', event => {
      $log.debug(`${NS} Message received from Service Worker`, event)

      this._serviceWorkerMessageReceived(event)
    })

    /**
     * Handler for the Service Worker controllerchange event.
     *
     * @listens ServiceWorkerGlobalScope.controllerchange
     * @param {event} controllerchangeEvent
     */
    this.serviceWorker.addEventListener('controllerchange', event => {
      $log.debug(`${NS} Service Worker controller changed`, event)

      this.emit(SERVICE_WORKER_EVENTS.SERVICE_WORKER_UPDATED)
    })
  }

  /**
   * Responsible for handling Service Worker messages.
   *
   * @private
   * @method _serviceWorkerMessageReceived
   *
   * @param {event} MessageEvent
   */
  _serviceWorkerMessageReceived(event) {
    if (!event || !event.data) {
      return
    }

    if (
      event.data.action === SERVICE_WORKER_EVENTS.SERVICE_WORKER_UPDATED &&
      this._refreshTriggered
    ) {
      this.emit(SERVICE_WORKER_EVENTS.SERVICE_WORKER_UPDATED)
    }
  }

  /**
   * Waits for the new Service Worker to be installed and then shows the soft refresh banner.
   *
   * @private
   * @method _trackServiceWorkerInstalling
   */
  _trackServiceWorkerInstalling(newServiceWorker) {
    /**
     * Handler for the Service Worker statechange event.
     *
     * @listens ServiceWorkerGlobalScope.statechange
     */
    newServiceWorker.addEventListener('statechange', () => {
      if (newServiceWorker.state === 'installed') {
        this.emit(SERVICE_WORKER_EVENTS.SERVICE_WORKER_INSTALLED)
      }
    })
  }
}
